import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Body1, Caption1, DisplayHours, EmptySlate } from "@activecollab/components";
import classNames from "classnames";
import { UsersColumn } from "~/components/Capacity/UsersColumn";
import { useCapacity } from "~/repository";
import { getPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getDecimalAndThousandSeparators } from "~/store/selectors/selector.languages";
import { getUsersByIds } from "~/store/selectors/selector.users";
import Angie from "~/utils/angie";
import { minutesToHours, minutesToHoursDecimal } from "~/utils/utils";
import { CapacityLoader } from "./CapacityLoader";
import { STATUS_GLOBAL_DAY_OFF, STATUS_NON_WORKING_DAYS, STATUS_NOT_AVAILABLE, STATUS_USER_DAY_OFF } from "./index";

const handleNoCapacity = (status) => {
  return status === STATUS_NOT_AVAILABLE || status === STATUS_NON_WORKING_DAYS || status === STATUS_GLOBAL_DAY_OFF;
};
export const CapacityTable = () => {
  const { t } = useTranslation();
  const capacityFilterObject = useSelector((state) => getPersonalizedConfigOption(state, "capacity_filter")).toJS();
  const { from, to, setRouteQueryState } = useOutletContext();

  const { data, isFetching, isError } = useCapacity({
    from,
    to,
    scale: capacityFilterObject?.scale ?? "weekly",
    user_ids: capacityFilterObject?.users ?? [],
  });

  const { users_data = [], columns = [], current_period, total_capacity } = data || {};

  const userIds = users_data.map((user) => user.user_id);
  const users = useSelector((state) => getUsersByIds(state, userIds));
  const separators = useSelector(getDecimalAndThousandSeparators);

  const sortedUsers = [...users_data].sort((a, b) => {
    const aName = users.get(`${a.user_id}`)?.get("display_name")?.toLowerCase() ?? "";
    const bName = users.get(`${b.user_id}`)?.get("display_name")?.toLowerCase() ?? "";

    return aName.localeCompare(bName);
  });

  useEffect(() => {
    setRouteQueryState({
      isFetching,
      isError,
      hasUsers: users_data.length > 0,
    });
  }, [isError, isFetching, setRouteQueryState, users_data.length]);

  if (isFetching) {
    return <CapacityLoader />;
  }

  return users_data.length > 0 && !isError ? (
    <div className="tw-flex tw-w-full tw-min-w-0">
      <UsersColumn
        usersData={sortedUsers}
        context="capacity-user-link"
        isFetching={isFetching}
      />

      <div className="tw-min-w-0 tw-overflow-x-auto tw-flex-grow">
        <div className="tw-min-w-max">
          <div className="tw-flex">
            {columns.map((col) => (
              <div
                key={col.key}
                className="tw-flex-[1_0_104px] tw-flex tw-flex-col [&:last-child>div:last-child]:tw-border-r-0"
              >
                <Caption1
                  className={classNames(
                    {
                      "capacity-current-period-column tw-bg-primary-300": col.key === current_period,
                    },
                    "tw-h-10 tw-w-full tw-flex tw-items-center tw-justify-center tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
                  )}
                  weight="bold"
                  color="secondary"
                >
                  {col.label}
                </Caption1>
                <Body1
                  weight="bold"
                  className="tw-h-10 tw-w-full tw-flex tw-items-center tw-justify-center tw-border-r tw-border-b tw-border-border-primary tw-border-solid tw-border-0 tw-bg-primary-200"
                >
                  {col.disabled ? "-" : minutesToHours(col.total_capacity, true)}
                </Body1>
              </div>
            ))}
          </div>

          {sortedUsers.map((user, index) => (
            <div
              key={user.user_id}
              className="tw-flex"
            >
              {columns.map((col) => {
                const cell = user.columns_data[col.key];

                return cell ? (
                  <Body1
                    key={col.key}
                    weight="bold"
                    color={cell.capacity > 0 && cell.status !== STATUS_USER_DAY_OFF ? "primary" : "alert"}
                    className={classNames(
                      {
                        "tw-bg-theme-200": index % 2 === 0,
                        "capacity-disabled-day": handleNoCapacity(cell.status),
                      },
                      "tw-h-10 tw-flex-[1_0_104px] tw-flex tw-items-center tw-justify-center tw-border-r tw-border-b tw-border-border-primary tw-border-solid tw-border-0 last:tw-border-r-0"
                    )}
                  >
                    {handleNoCapacity(cell.status) ? null : (
                      <>{cell.status === STATUS_USER_DAY_OFF ? "OFF" : minutesToHours(cell.capacity, true)}</>
                    )}
                  </Body1>
                ) : null;
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="tw-w-[104px] tw-shrink-0">
        <Caption1
          weight="bold"
          color="secondary"
          className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-border-primary tw-border-solid tw-border-0"
        >
          {t("Total")}
        </Caption1>

        <DisplayHours
          value={minutesToHoursDecimal(total_capacity)}
          variant="Body 1"
          weight="bold"
          format={minutesToHoursDecimal(total_capacity) < 10000 ? "long" : "short"}
          decimalSeparator={separators.decimal_separator}
          thousandSeparator={separators.thousand_separator}
          trimZeroMinutes
          className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-l tw-border-border-primary tw-border-solid tw-border-0 tw-bg-primary-200"
        />
        {sortedUsers.map((user) => (
          <Body1
            key={user.user_id}
            weight="bold"
            className="tw-h-10 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-l tw-border-border-primary tw-border-solid tw-border-0 tw-bg-primary-200"
          >
            {minutesToHours(user.total_capacity, true)}
          </Body1>
        ))}
      </div>
    </div>
  ) : (
    <EmptySlate
      title={t("No Data")}
      src={`${Angie.assets_url}/environment/images/layout/search_empty_page.png`}
      alt="Capacity"
      description={t("Adjust your filters and run the report again.")}
      className="tw-my-auto"
    />
  );
};
